import { CommonModule, Location } from '@angular/common';
import { Component, type OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DeploySystemFrontendReviewRole,
  DeploySystemReviewDetail,
  DeploySystemReviewPayload,
  DeploySystemReviewRoleBack,
  DeploySystemReviewStage,
  DeploySystemReviewUserData,
} from '@/app/interfaces/deploy-system-review';
import { SystemsService } from '@/app/services/ia-services/systems.service';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { PillComponent } from '@/app/shared/pill/pill.component';
import {
  ReviewComment,
  ReviewCommentsBlockComponent,
} from '@/app/shared/review-comments-block/review-comments-block.component';
import { getTaskFunctionLabel as mapTaskFunctionLabel } from '@/app/shared/mappers/task-function-label.mapper';
import { mapSystemTypeLabel } from '@/app/shared/mappers/system-type-label.mapper';
import { SystemDeployEconomicDataComponent } from '../components/economic-data/economic-data.component';
import { SystemDeployHapComplianceComponent } from '../components/hap-compliance/hap-compliance.component';
import { SystemDeployImpactEvaluationComponent } from '../components/impact-evaluation/impact-evaluation.component';
import { SystemDeployIsoComplianceComponent } from '../components/iso-compliance/iso-compliance.component';
import { SystemDeployRiaComplianceComponent } from '../components/ria-compliance/ria-compliance.component';
import { SystemDeployRiskManagementComponent } from '../components/risk-management/risk-management.component';
import { SystemDeployTechnicalIssuesComponent } from '../components/technical-issues/technical-issues.component';
import { environment } from '@/environments/environment';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SystemDeployReviewFieldsService } from '@/app/services/ia-services/system-deploy-review-fields.service';

type DeploySystemReviewState = 'CORRECTION' | 'APPROVED';

@Component({
  selector: 'app-deploy-system-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonsComponent,
    ConfirmDialogModule,
    SystemDeployEconomicDataComponent,
    SystemDeployHapComplianceComponent,
    SystemDeployImpactEvaluationComponent,
    SystemDeployIsoComplianceComponent,
    PillComponent,
    ReviewCommentsBlockComponent,
    SystemDeployRiaComplianceComponent,
    SystemDeployRiskManagementComponent,
    SystemDeployTechnicalIssuesComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './deploy-system-review.component.html',
  styleUrl: './deploy-system-review.component.scss',
})
export class DeploySystemReviewComponent implements OnInit {
  detailDeploySystem: DeploySystemReviewDetail | null = null;
  idSystem: string | null = null;
  stateGroups: { group: DeploySystemReviewStage; state: string }[] = [];
  userRoles: DeploySystemFrontendReviewRole[] = [];
  activeUserRole: DeploySystemFrontendReviewRole | null = null;
  activeBackendRole: DeploySystemReviewRoleBack | null = null;
  reviewComment = '';
  reviewComments: ReviewComment[] = [];
  readonlyReview = false;
  selectedState: DeploySystemReviewState | null = null;

  constructor(
    private readonly systemsService: SystemsService,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly confirmationService: ConfirmationService,
    private readonly router: Router,
    private readonly reviewFields: SystemDeployReviewFieldsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.idSystem = this.route.snapshot.paramMap.get('id');
    const initialDetail = await this.systemsService.getDeploySystemReviewDetail(this.idSystem ?? '');
    this.userRoles = this.getUserRolesForExecution(initialDetail);
    this.activeUserRole = this.resolveActiveUserRole(initialDetail, this.userRoles);
    this.activeBackendRole = this.activeUserRole ? this.mapUserRoleToBackend(this.activeUserRole) : null;

    const response = this.activeBackendRole
      ? await this.systemsService.getDeploySystemReviewDetail(this.idSystem ?? '', this.activeBackendRole)
      : initialDetail;

    if (response?.infoCard) {
      response.infoCard.forEach((card) => {
        card.buttons = [
          {
            label: 'Consultar detalles',
            url: 'catalogo-ia/proyectos/detalles/' + card.id,
          },
        ];
      });
    }

    this.detailDeploySystem = response;
    this.reviewFields.loadPreviousReviewsFromBackend(response);
    this.stateGroups = this.buildStateGroups(response);
    this.readonlyReview = this.shouldOpenAsReadonlyReview();
    if (this.isDeploySystemCorrectionState() && !this.isCurrentReviewRegistered()) {
      await this.router.navigate(['/catalogo-ia/sistemas-de-ia/despliegue/detalles', this.idSystem]);
      return;
    }

    this.reviewComments = this.buildReviewComments(response);
  }

  backPage(): void {
    this.location.back();
  }

  confirm(state: DeploySystemReviewState): void {
    if (this.readonlyReview) {
      return;
    }

    if (state === 'APPROVED' && this.isChief() && this.reviewFields.getMarkedFieldsForBackend().length > 0) {
      return;
    }

    this.selectedState = state;

    this.confirmationService.confirm({
      message: '¿Seguro que desea finalizar revisión?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Guardar',
      },
      accept: () => {
        void this.onSave();
      },
    });
  }

  async onSave(): Promise<void> {
    if (
      !this.selectedState ||
      !this.activeBackendRole ||
      !this.detailDeploySystem ||
      this.detailDeploySystem.executionSystemId == null
    ) {
      return;
    }

    const payload: DeploySystemReviewPayload = {
      idEjecucionSistema: this.detailDeploySystem.executionSystemId,
      nombreEjecucionSistema: this.detailDeploySystem.systemName ?? '',
      userRole: this.activeBackendRole,
      state: this.selectedState,
      comentario: this.reviewComment,
      camposSeleccionados: this.reviewFields.getMarkedFieldsForBackend(),
    };

    await this.systemsService.changeDeploySystemReviewState(payload);
    await this.router.navigate(['/catalogo-ia/sistemas-de-ia/listado-sistemas']);
  }

  isChief(): boolean {
    return this.userRoles.includes('jefe_servicio_OGIA');
  }

  isSupportGroup(): boolean {
    return this.userRoles.includes('apoyo_OGIA');
  }

  protected hasMarkedFields(): boolean {
    return this.reviewFields.getMarkedFieldsForBackend().length > 0;
  }

  get shouldShowReviewComments(): boolean {
    return this.reviewComments.length > 0 && (!this.isSupportGroup() || this.readonlyReview);
  }

  get activeStage(): DeploySystemReviewStage | undefined {
    return this.stateGroups.find((stage) => stage.state === 'PENDING')?.group;
  }

  getSystemTypeLabel(value: string | null | undefined): string {
    return value ? mapSystemTypeLabel(value) : '-';
  }

  getFunctionLabel(value: string | null | undefined): string {
    return value ? mapTaskFunctionLabel(value) : '-';
  }

  private buildStateGroups(detail: DeploySystemReviewDetail): { group: DeploySystemReviewStage; state: string }[] {
    return [
      { group: 'support', state: this.normalizeReviewState(detail.supportGroupState) },
      { group: 'reviewers', state: this.buildReviewersState(detail) },
      { group: 'chief', state: this.normalizeReviewState(detail.serviceManagerState) },
    ];
  }

  private buildReviewComments(detail: DeploySystemReviewDetail): ReviewComment[] {
    if (this.activeBackendRole === 'GA' && this.readonlyReview) {
      return [
        {
          userRole: 'GA',
          comment: detail.commentSupportGroup ?? null,
          state: detail.supportGroupState ?? null,
        },
      ];
    }
    const reviewComments: ReviewComment[] = [
      {
        userRole: 'GA',
        comment: detail.commentSupportGroup ?? null,
        state: detail.supportGroupState ?? null,
      },
      {
        userRole: 'RF',
        comment: detail.commentRevisorFuncional ?? null,
        state: detail.functionalReviewerState ?? null,
      },
      {
        userRole: 'RJ',
        comment: detail.commentRevisorJuridico ?? null,
        state: detail.legalReviewerState ?? null,
      },
      {
        userRole: 'RT',
        comment: detail.commentRevisorTecnico ?? null,
        state: detail.technicalReviewerState ?? null,
      },
      {
        userRole: 'RDPD',
        comment: detail.commentRevisorDpd ?? null,
        state: detail.dpdReviewerState ?? null,
      },
    ];

    if (this.isChief()) {
      reviewComments.push({
        userRole: 'JS',
        comment: detail.commentServiceManager ?? null,
        state: detail.serviceManagerState ?? null,
      });
    }

    return reviewComments;
  }

  private buildReviewersState(detail: DeploySystemReviewDetail): string {
    const states = [
      detail.technicalReviewerState,
      detail.legalReviewerState,
      detail.functionalReviewerState,
      detail.dpdReviewerState,
    ].map((state) => this.normalizeReviewState(state));

    return states.every((state) => state !== 'PENDING') ? 'APPROVED' : 'PENDING';
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private getUserRolesForExecution(detail: DeploySystemReviewDetail): DeploySystemFrontendReviewRole[] {
    const userData = this.getStoredUserData();
    const executionOrgs = [detail.orgCodeCreator]
      .filter((org): org is string => !!org)
      .map((org) => this.normalizeOrgCodeForEnvironment(org));

    return Object.entries(userData.roles ?? {})
      .filter(
        ([, orgs]) =>
          Array.isArray(orgs) && orgs.some((org) => executionOrgs.includes(this.normalizeOrgCodeForEnvironment(org)))
      )
      .map(([role]) => role)
      .filter((role): role is DeploySystemFrontendReviewRole => this.isDeploySystemFrontendReviewRole(role));
  }

  private normalizeOrgCodeForEnvironment(orgCode: string): string {
    return orgCode.startsWith(environment.environment) ? orgCode : `${environment.environment}${orgCode}`;
  }

  private resolveActiveUserRole(
    detail: DeploySystemReviewDetail,
    userRoles: DeploySystemFrontendReviewRole[]
  ): DeploySystemFrontendReviewRole | null {
    if (userRoles.includes('jefe_servicio_OGIA')) {
      return 'jefe_servicio_OGIA';
    }

    const activeStage = this.buildStateGroups(detail).find((stage) => stage.state === 'PENDING')?.group;
    const pendingRole = activeStage ? this.getPendingUserRoleForStage(activeStage, detail, userRoles) : null;

    return pendingRole ?? this.getRegisteredUserRole(detail, userRoles);
  }

  private getPendingUserRoleForStage(
    stage: DeploySystemReviewStage,
    detail: DeploySystemReviewDetail,
    userRoles: DeploySystemFrontendReviewRole[]
  ): DeploySystemFrontendReviewRole | null {
    switch (stage) {
      case 'support':
        return userRoles.includes('apoyo_OGIA') && this.isRoleStatePending(detail, 'apoyo_OGIA') ? 'apoyo_OGIA' : null;
      case 'reviewers':
        return (
          this.getReviewerRolesInWorkflowOrder(userRoles).find((role) => this.isRoleStatePending(detail, role)) ?? null
        );
      case 'chief':
        return userRoles.includes('jefe_servicio_OGIA') && this.isRoleStatePending(detail, 'jefe_servicio_OGIA')
          ? 'jefe_servicio_OGIA'
          : null;
      default:
        return null;
    }
  }

  private getRegisteredUserRole(
    detail: DeploySystemReviewDetail,
    userRoles: DeploySystemFrontendReviewRole[]
  ): DeploySystemFrontendReviewRole | null {
    return (
      userRoles.find((role) => {
        const state = this.getReviewStateForUserRole(detail, role);
        return state === 'APPROVED' || state === 'CORRECTION';
      }) ?? null
    );
  }

  private getReviewerRolesInWorkflowOrder(
    userRoles: DeploySystemFrontendReviewRole[]
  ): DeploySystemFrontendReviewRole[] {
    const reviewerRoles: DeploySystemFrontendReviewRole[] = [
      'funcional_OGIA',
      'juridico_OGIA',
      'tecnico_OGIA',
      'dpd_OGIA',
    ];

    return reviewerRoles.filter((role) => userRoles.includes(role));
  }

  private isRoleStatePending(detail: DeploySystemReviewDetail, role: DeploySystemFrontendReviewRole): boolean {
    return this.getReviewStateForUserRole(detail, role) === 'PENDING';
  }

  private getReviewStateForUserRole(detail: DeploySystemReviewDetail, role: DeploySystemFrontendReviewRole): string {
    const stateByRole: Record<DeploySystemFrontendReviewRole, string | null | undefined> = {
      apoyo_OGIA: detail.supportGroupState,
      funcional_OGIA: detail.functionalReviewerState,
      juridico_OGIA: detail.legalReviewerState,
      tecnico_OGIA: detail.technicalReviewerState,
      dpd_OGIA: detail.dpdReviewerState,
      jefe_servicio_OGIA: detail.serviceManagerState,
    };

    return this.normalizeReviewState(stateByRole[role]);
  }

  private mapUserRoleToBackend(userRole: DeploySystemFrontendReviewRole): DeploySystemReviewRoleBack | null {
    switch (userRole) {
      case 'apoyo_OGIA':
        return 'GA';
      case 'funcional_OGIA':
        return 'RF';
      case 'juridico_OGIA':
        return 'RJ';
      case 'dpd_OGIA':
        return 'RDPD';
      case 'tecnico_OGIA':
        return 'RT';
      case 'jefe_servicio_OGIA':
        return 'JS';
      default:
        return null;
    }
  }

  private isDeploySystemFrontendReviewRole(role: string): role is DeploySystemFrontendReviewRole {
    return (
      role === 'apoyo_OGIA' ||
      role === 'funcional_OGIA' ||
      role === 'juridico_OGIA' ||
      role === 'tecnico_OGIA' ||
      role === 'dpd_OGIA' ||
      role === 'jefe_servicio_OGIA'
    );
  }

  private getStoredUserData(): DeploySystemReviewUserData {
    const storedUserData = localStorage.getItem('userData');

    if (!storedUserData) {
      return {};
    }

    return JSON.parse(storedUserData) as DeploySystemReviewUserData;
  }

  private getCurrentReviewState(): string {
    if (!this.detailDeploySystem || !this.activeUserRole) {
      return '';
    }

    return this.getReviewStateForUserRole(this.detailDeploySystem, this.activeUserRole);
  }

  private isCurrentReviewRegistered(): boolean {
    const currentState = this.getCurrentReviewState();

    return currentState === 'APPROVED' || currentState === 'CORRECTION';
  }

  private shouldOpenAsReadonlyReview(): boolean {
    if (!this.isCurrentReviewRegistered()) {
      return false;
    }

    return !this.isChief() || this.isDeploySystemCorrectionState();
  }

  private isDeploySystemCorrectionState(): boolean {
    return this.detailDeploySystem?.state?.toUpperCase() === 'CORRECTION';
  }

  shouldShowPreviousReviewIndicators(): boolean {
    if (!this.isChief()) {
      return false;
    }

    return !this.readonlyReview || this.isDeploySystemCorrectionState();
  }
}
